import { MapElement } from '../../../types'

interface BoundingBox {
  minX: number
  minY: number
  maxX: number
  maxY: number
  width: number
  height: number
}

const getElementBounds = (element: MapElement): BoundingBox => {
  const { position, properties, type } = element
  let minX = position.x
  let minY = position.y
  let maxX = position.x
  let maxY = position.y

  switch (type) {
    case 'wall':
    case 'door':
    case 'window':
    case 'stairs':
    case 'line':
    case 'fixture-line':
    case 'measurement':
      // Line-based elements
      if (properties.endPosition) {
        minX = Math.min(position.x, properties.endPosition.x)
        minY = Math.min(position.y, properties.endPosition.y)
        maxX = Math.max(position.x, properties.endPosition.x)
        maxY = Math.max(position.y, properties.endPosition.y)
      }
      break

    case 'rectangle':
    case 'fixture-rect':
      // Rectangle elements
      if (properties.width && properties.height) {
        maxX = position.x + properties.width
        maxY = position.y + properties.height
      }
      break

    case 'circle':
    case 'fixture-circle':
      // Circle elements
      if (properties.radius) {
        minX = position.x - properties.radius
        minY = position.y - properties.radius
        maxX = position.x + properties.radius
        maxY = position.y + properties.radius
      }
      break

    case 'polygon':
    case 'room':
    case 'fixture-polygon':
      // Polygon elements
      if (properties.vertices && Array.isArray(properties.vertices)) {
        for (const vertex of properties.vertices) {
          minX = Math.min(minX, vertex.x)
          minY = Math.min(minY, vertex.y)
          maxX = Math.max(maxX, vertex.x)
          maxY = Math.max(maxY, vertex.y)
        }
      }
      break

    case 'text':
      // Text elements - estimate bounds based on font size and text length
      const fontSize = properties.fontSize || 16
      const text = properties.text || ''
      const estimatedWidth = text.length * fontSize * 0.6 // Rough estimate
      const estimatedHeight = fontSize * 1.2

      minX = position.x - estimatedWidth / 2
      minY = position.y - estimatedHeight / 2
      maxX = position.x + estimatedWidth / 2
      maxY = position.y + estimatedHeight / 2
      break

    case 'poi':
      // Point of Interest - typically has a fixed size icon
      const poiSize = 30 // Default POI icon size
      minX = position.x - poiSize / 2
      minY = position.y - poiSize / 2
      maxX = position.x + poiSize / 2
      maxY = position.y + poiSize / 2
      break

    case 'fixture-point':
      // Point fixtures
      const pointSize = 10
      minX = position.x - pointSize / 2
      minY = position.y - pointSize / 2
      maxX = position.x + pointSize / 2
      maxY = position.y + pointSize / 2
      break
  }

  // Account for stroke width if present
  const strokeWidth = properties.strokeWidth || 2
  minX -= strokeWidth / 2
  minY -= strokeWidth / 2
  maxX += strokeWidth / 2
  maxY += strokeWidth / 2

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  }
}

export const calculateContentBounds = (
  elements: MapElement[],
): BoundingBox | null => {
  if (elements.length === 0) {
    return null
  }

  let globalMinX = Infinity
  let globalMinY = Infinity
  let globalMaxX = -Infinity
  let globalMaxY = -Infinity

  for (const element of elements) {
    const bounds = getElementBounds(element)
    globalMinX = Math.min(globalMinX, bounds.minX)
    globalMinY = Math.min(globalMinY, bounds.minY)
    globalMaxX = Math.max(globalMaxX, bounds.maxX)
    globalMaxY = Math.max(globalMaxY, bounds.maxY)
  }

  // Add padding around the content
  const padding = 20
  globalMinX -= padding
  globalMinY -= padding
  globalMaxX += padding
  globalMaxY += padding

  return {
    minX: globalMinX,
    minY: globalMinY,
    maxX: globalMaxX,
    maxY: globalMaxY,
    width: globalMaxX - globalMinX,
    height: globalMaxY - globalMinY,
  }
}

export const captureContentArea = (
  canvas: HTMLCanvasElement,
  elements: MapElement[],
  zoom: number,
  panOffset: { x: number; y: number },
): HTMLCanvasElement | null => {
  const contentBounds = calculateContentBounds(elements)
  if (!contentBounds) return null

  // Create a new canvas for the content area
  const contentCanvas = document.createElement('canvas')
  const contentCtx = contentCanvas.getContext('2d')
  if (!contentCtx) return null

  // Set canvas size to match content bounds
  contentCanvas.width = contentBounds.width * zoom
  contentCanvas.height = contentBounds.height * zoom

  // Calculate the source rectangle from the original canvas
  const sourceX = (contentBounds.minX - panOffset.x) * zoom
  const sourceY = (contentBounds.minY - panOffset.y) * zoom
  const sourceWidth = contentBounds.width * zoom
  const sourceHeight = contentBounds.height * zoom

  // Set white background
  contentCtx.fillStyle = 'white'
  contentCtx.fillRect(0, 0, contentCanvas.width, contentCanvas.height)

  // Draw the content area from the original canvas
  try {
    contentCtx.drawImage(
      canvas,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      contentCanvas.width,
      contentCanvas.height,
    )
  } catch (error) {
    console.error('Error capturing content area:', error)
    return null
  }

  return contentCanvas
}
